import { useState } from 'react';
import { useMany, useTranslate, type CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Alert, Table, DatePicker, Flex, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import type {
  PublisherListOut,
  RoyaltyMonthlyOut,
} from '@/api/generated/schemas';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { orDash } from '@/components/common/show/shared';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { ProductSelectFilter } from '@/components/user_catalog/publications/ProductSelectFilter';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { useAdminTable } from '@/hooks/useAdminTable';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';
import { formatDuration } from '@/utils/duration';
import { formatUnits } from '@/utils/format';
import { formatLedger, formatPayable } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { UnitPriceCell } from '@/components/settlement/UnitPriceCell';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';

const T = 'settlement.royalties_monthly';

export const RoyaltiesMonthlyList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();

  const [selectedMonth, setSelectedMonth] = useState(() =>
    dayjs().startOf('month')
  );

  const { tableProps, sorters, filters, setFilters } =
    useAdminTable<RoyaltyMonthlyOut>({
      pagination: { pageSize: 20 },
      resource: 'settlement.royalties_monthly',
      filters: {
        initial: [
          {
            field: 'bucket',
            operator: 'eq',
            value: dayjs().startOf('month').format('YYYY-MM-DD'),
          },
        ],
      },
      syncWithLocation: true,
    });

  const rows = tableProps.dataSource ?? [];

  // Batched once per page: the row components take the record and skip their
  // own fetch, so a page costs one extra request instead of one per row.
  const publisherIds = [
    ...new Set(rows.map((r) => r.publisher_id).filter(Boolean)),
  ];

  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });

  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const productIds = [
    ...new Set(rows.map((r) => r.product_id).filter(Boolean)),
  ];

  const publicationByProduct = usePublicationsByProducts(productIds);

  const handleMonthChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    setSelectedMonth(date);
    setFilters(
      [
        ...filters.filter(
          (f) => !('field' in f) || (f as { field: string }).field !== 'bucket'
        ),
        { field: 'bucket', operator: 'eq', value: date.format('YYYY-MM-DD') },
      ],
      'replace'
    );
  };

  return (
    <List canCreate={false}>
      <Alert
        type="warning"
        showIcon
        message={t(`${T}.intro`)}
        style={{ marginBottom: 16 }}
      />
      {/* The month is chosen here, so no bucket column — every row shares it. */}
      <div style={{ marginBottom: 16 }}>
        <DatePicker
          picker="month"
          value={selectedMonth}
          onChange={handleMonthChange}
          allowClear={false}
        />
      </div>
      <Table
        {...rowNav('settlement.royalties_monthly', tableProps)}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        scroll={{ x: 'max-content' }}
      >
        {/* First, so it can stay pinned left — fixed columns lead the row. */}
        <Table.Column
          dataIndex="product_id"
          title={t(`${T}.fields.product_id`)}
          fixed="left"
          width={320}
          filtered={isFieldFiltered(filters as CrudFilter[], 'product_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <ProductSelectFilter
              field="product_id"
              placeholder={t(`${T}.fields.product_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number, record: RoyaltyMonthlyOut) => {
            const publication = publicationByProduct.get(value);
            const kind = productKind(publication, value);
            return orDash(
              value != null && (
                <Flex vertical gap={4} align="flex-start">
                  <Flex gap={4} align="center" wrap>
                    {publication ? (
                      <PublicationInfoPopover
                        publication={publication}
                        publicationId={publication.id}
                      />
                    ) : (
                      value
                    )}
                    <Typography.Text type="secondary">
                      {`v${record.product_version}`}
                    </Typography.Text>
                  </Flex>
                  {kind && <ProductFormatTag kind={kind} />}
                </Flex>
              )
            );
          }}
        />
        <Table.Column
          dataIndex="publisher_id"
          title={t(`${T}.fields.publisher_id`)}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publisher_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="publisher_id"
              only="imprints"
              placeholder={t(`${T}.fields.publisher_id`)}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: number) =>
            orDash(
              value != null && (
                <PublisherIconLink
                  publisherId={value}
                  publisher={publisherMap.get(value)}
                />
              )
            )
          }
        />
        <Table.Column
          dataIndex="product_duration"
          title={t(`${T}.fields.product_duration`)}
          render={(value: number) => (
            <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
          )}
        />
        <Table.Column
          dataIndex="rate_value"
          title={t(`${T}.fields.rate_value`)}
          align="right"
          render={(value: string, record: RoyaltyMonthlyOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, record.currency)}
            </span>
          )}
        />
        {/* Column titles drop the "unit price" words — the group header
            carries them. */}
        <Table.ColumnGroup title={t(`${T}.fields.unit_price`)}>
          <Table.Column
            dataIndex="unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.ledger`)}
                hint={t('settlement.hints.unit_price')}
              />
            }
            align="right"
            render={(value: string, record: RoyaltyMonthlyOut) => (
              <UnitPriceCell unitPrice={value} currency={record.currency} />
            )}
          />
          <Table.Column
            dataIndex="payable_unit_price"
            title={
              <HintLabel
                label={t(`${T}.columns.invoice`)}
                hint={t('settlement.hints.payable_unit_price')}
              />
            }
            align="right"
            render={(value: string, record: RoyaltyMonthlyOut) => (
              <span style={NUMERIC_STYLE}>
                {formatPayable(value, record.currency)}
              </span>
            )}
          />
        </Table.ColumnGroup>
        <Table.Column
          dataIndex="basis_seconds"
          title={t(`${T}.fields.basis_seconds`)}
          sorter
          sortOrder={getSortOrder(sorters, 'basis_seconds')}
          render={(value: number) => (
            <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
          )}
        />
        <Table.Column
          dataIndex="charges_count"
          title={t(`${T}.fields.charges_count`)}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'charges_count')}
        />
        <Table.Column
          dataIndex="consumers_count"
          title={t(`${T}.fields.consumers_count`)}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'consumers_count')}
        />
        <Table.Column
          dataIndex="units_count"
          title={t(`${T}.fields.units_count`)}
          align="right"
          sorter
          sortOrder={getSortOrder(sorters, 'units_count')}
          render={(value: string) => (
            <span style={NUMERIC_STYLE}>{formatUnits(value)}</span>
          )}
        />
        {/* Column titles drop the "amount" word — the group header carries
            it. Pinned right as a whole; the children inherit `fixed`. */}
        <Table.ColumnGroup title={t(`${T}.fields.amount`)} fixed="right">
          <Table.Column
            dataIndex="amount"
            title={
              <HintLabel
                label={t(`${T}.columns.ledger`)}
                hint={t('settlement.hints.amount_ledger')}
              />
            }
            align="right"
            sorter
            sortOrder={getSortOrder(sorters, 'amount')}
            render={(value: string, record: RoyaltyMonthlyOut) => (
              <span style={NUMERIC_STYLE}>
                {formatLedger(value, record.currency)}
              </span>
            )}
          />
          {/* The API cannot sort on the invoice amount — it is not in the
              endpoint's sort enum, and it is a sum of rounded lines, not a
              column it could order by. */}
          <Table.Column
            dataIndex="payable_amount"
            title={
              <HintLabel
                label={t(`${T}.columns.invoice`)}
                hint={t('settlement.hints.payable_amount')}
              />
            }
            align="right"
            render={(value: string, record: RoyaltyMonthlyOut) => (
              <PayableAmount
                amount={value}
                roundingDelta={record.rounding_delta}
                currency={record.currency}
              />
            )}
          />
        </Table.ColumnGroup>
      </Table>
    </List>
  );
};
